import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { forOwn, isUndefined } from 'lodash';
import { combineLatest, distinctUntilChanged, filter, map } from 'rxjs';
import { assertStore } from '../../cross-team-shared/misc-utils';
import { isOrthographicVideoOutput } from '../../cross-team-shared/misc-utils/output-utils';
import { shellConnectorToken } from '../../team-apis/core/shell-connector';
import { Point2D } from '../../team-apis/otterdam/answers/shape-typings';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import {
  backViewRegionName,
  mainViewRegionName,
  orthographicViewRegionName,
  sideViewRegionName,
  topViewRegionName,
} from '../state/view-region-state';
import {
  calculateMainRegionLowerLeftCorner,
  calculateMainViewSize,
  getBackViewRegion,
  getSideViewRegion,
  getTopViewRegion,
  ViewRegionConfiguration,
} from './view-region-utils';

export const bottomEditPanelHeight = 272;
export const editPanelToolbarHeight = 28;
export const rescaleAllToggleHeight = 43;

@Injectable()
export class ViewRegionsService extends InjectableSuperclass {
  private readonly store = inject(PointCloudContentStore);
  private readonly shellConnector = inject(shellConnectorToken);

  private areEditPanelsHidden$ = combineLatest([
    this.store('viewRegions')('isMainViewInThumbnailMode').$,
    this.store('viewRegions')('hasEditPanelsOpen').$,
    this.store.focusedShape$,
  ]).pipe(
    map(
      ([isMainViewInThumbnailMode, hasEditPanelsOpen, focusedShape]) =>
        isUndefined(focusedShape) ||
        isMainViewInThumbnailMode ||
        !hasEditPanelsOpen,
    ),
    distinctUntilChanged(),
  );

  private hasBottomEditPanels$ = this.store('editPanel')('location').$.pipe(
    map((location) => location === 'bottom'),
  );

  private isEditPanelMinimized$ = combineLatest([
    this.hasBottomEditPanels$,
    this.store('editPanel')('isExpanded').$,
  ]).pipe(
    map(
      ([hasBottomEditPanels, isEditPanelExpanded]) =>
        hasBottomEditPanels && !isEditPanelExpanded,
    ),
  );

  private areBottomPanelsHidden$ = combineLatest([
    this.areEditPanelsHidden$,
    this.hasBottomEditPanels$,
    this.isEditPanelMinimized$,
  ]).pipe(
    map(
      ([areEditPanelsHidden, hasBottomEditPanels, isEditPanelMinimized]) =>
        !hasBottomEditPanels || isEditPanelMinimized || areEditPanelsHidden,
    ),
  );

  private sceneSize$ = this.store('render')('containerSize')(
    this.store.state().output.name,
  ).$.pipe(filter(isDefined));

  constructor() {
    super();
    this.subscribeToMainViewRegionChanges();
    this.subscribeToEditPanelRegionChanges();
    this.subscribeToOrthographicRegionChanges();
    this.subscribeToProjectionRegionChanges();
  }

  private subscribeToMainViewRegionChanges() {
    this.subscribeTo(
      combineLatest([
        this.areBottomPanelsHidden$,
        this.sceneSize$,
        this.areEditPanelsHidden$,
      ]),
      ([areBottomPanelsHidden, sceneSize]) => {
        const viewRegionStore =
          this.store('viewRegions')('regions')(mainViewRegionName);
        assertStore(viewRegionStore, 'main view region must exist');
        viewRegionStore.setUsing((state) => ({
          ...state,
          size: calculateMainViewSize(areBottomPanelsHidden, sceneSize),
          lowerLeftCorner: calculateMainRegionLowerLeftCorner(
            areBottomPanelsHidden,
          ),
        }));
      },
    );
  }

  private subscribeToEditPanelRegionChanges() {
    this.subscribeTo(
      combineLatest([
        this.hasBottomEditPanels$,
        this.sceneSize$,
        this.areBottomPanelsHidden$,
        this.areEditPanelsHidden$,
      ]),
      ([hasBottomEditPanels, sceneSize, areBottomPanelsHidden]) => {
        if (hasBottomEditPanels) {
          return this.updateViewRegionsForBottomEditPanels(
            sceneSize,
            areBottomPanelsHidden,
          );
        }
        return this.updateViewRegionsForSideEditPanels(sceneSize);
      },
    );
  }

  private subscribeToOrthographicRegionChanges() {
    const orthographicOutput = this.store
      .state()
      .project.outputs.find(isOrthographicVideoOutput);
    if (isUndefined(orthographicOutput)) {
      return;
    }

    this.subscribeTo(
      this.store('render')('containerSize')(orthographicOutput.name).$.pipe(
        filter(isDefined),
      ),
      (containerSize) => {
        const viewRegionStore = this.store('viewRegions')('regions')(
          orthographicViewRegionName,
        );
        assertStore(viewRegionStore, 'orthographic view region must exist');
        viewRegionStore.setUsing((state) => ({
          ...state,
          size: containerSize,
        }));
      },
    );
  }

  private subscribeToProjectionRegionChanges() {
    this.subscribeTo(
      this.store('projectionImageSize').$,
      (projectionImageSize) => {
        forOwn(projectionImageSize, (size, outputName) => {
          if (!size) {
            return;
          }
          const projectionViewRegionStore =
            this.store('viewRegions')('regions')(outputName);
          assertStore(
            projectionViewRegionStore,
            'projection renderer must have a view region',
          );

          projectionViewRegionStore.setUsing((state) => ({ ...state, size }));
        });
      },
    );
  }

  private updateViewRegionsForBottomEditPanels(
    sceneSize: Point2D,
    areBottomPanelsHidden: boolean,
  ) {
    const viewRegionsStore = this.store('viewRegions')('regions');
    const topViewRegionStore = viewRegionsStore(topViewRegionName);
    const backViewRegionStore = viewRegionsStore(backViewRegionName);
    const sideViewRegionStore = viewRegionsStore(sideViewRegionName);
    assertStore(topViewRegionStore, 'top view region must exist');
    assertStore(backViewRegionStore, 'back view region must exist');
    assertStore(sideViewRegionStore, 'side view region must exist');

    const sharedConfiguration: ViewRegionConfiguration = {
      isBottomPanel: true,
      sceneSize,
      readonly: this.store.state().readOnly,
      isNewEditPanel: this.shellConnector.hasFeatureFlag('new-edit-panel'),
    };

    backViewRegionStore.setUsing((state) => ({
      ...state,
      ...getBackViewRegion(sharedConfiguration),
    }));

    sideViewRegionStore.setUsing((state) => ({
      ...state,
      ...getSideViewRegion(sharedConfiguration),
    }));

    topViewRegionStore.setUsing((state) => ({
      ...state,
      ...getTopViewRegion({ ...sharedConfiguration, areBottomPanelsHidden }),
    }));
  }

  private updateViewRegionsForSideEditPanels(sceneSize: Point2D) {
    const viewRegionsStore = this.store('viewRegions')('regions');
    const topViewRegionStore = viewRegionsStore(topViewRegionName);
    const backViewRegionStore = viewRegionsStore(backViewRegionName);
    const sideViewRegionStore = viewRegionsStore(sideViewRegionName);
    assertStore(topViewRegionStore, 'top view region must exist');
    assertStore(backViewRegionStore, 'back view region must exist');
    assertStore(sideViewRegionStore, 'side view region must exist');

    const sharedConfiguration: ViewRegionConfiguration = {
      isBottomPanel: false,
      sceneSize,
      readonly: this.store.state().readOnly,
      isNewEditPanel: this.shellConnector.hasFeatureFlag('new-edit-panel'),
    };

    sideViewRegionStore.setUsing((state) => ({
      ...state,
      ...getSideViewRegion(sharedConfiguration),
    }));

    backViewRegionStore.setUsing((state) => ({
      ...state,
      ...getBackViewRegion(sharedConfiguration),
    }));

    topViewRegionStore.setUsing((state) => ({
      ...state,
      ...getTopViewRegion(sharedConfiguration),
    }));
  }
}
